import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../../components/navigation/AdminHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import RequestQueueCard from './components/RequestQueueCard';
import FilterBar from './components/FilterBar';
import MetricsBar from './components/MetricsBar';

const TechnicianPortalDashboard = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    urgency: 'all',
    issueType: 'all',
    status: 'all',
    proximity: 'all'
  });

  useEffect(() => {
    fetchRequests();
    setupRealtimeSubscription();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [requests, filters]);

  const fetchRequests = async () => {
    try {
      const { data, error } = await supabase
        ?.from('service_requests')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = supabase
      ?.channel('service_requests_changes')
      ?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'service_requests' },
        (payload) => {
          if (payload?.eventType === 'INSERT') {
            setRequests(prev => [payload?.new, ...prev]);
          } else if (payload?.eventType === 'UPDATE') {
            setRequests(prev =>
              prev?.map(req => req?.id === payload?.new?.id ? payload?.new : req)
            );
          } else if (payload?.eventType === 'DELETE') {
            setRequests(prev => prev?.filter(req => req?.id !== payload?.old?.id));
          }
        }
      )
      ?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  };

  const applyFilters = () => {
    let filtered = [...requests];

    if (filters?.urgency !== 'all') {
      filtered = filtered?.filter(req => req?.urgency === filters?.urgency);
    }

    if (filters?.issueType !== 'all') {
      filtered = filtered?.filter(req => req?.issue_type === filters?.issueType);
    }

    if (filters?.status !== 'all') {
      filtered = filtered?.filter(req => req?.status === filters?.status);
    }

    setFilteredRequests(filtered);
  };

  const handlePullToRefresh = async () => {
    setRefreshing(true);
    await fetchRequests();
    setRefreshing(false);
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    try {
      const { error } = await supabase
        ?.from('service_requests')
        ?.update({ status: newStatus, updated_at: new Date()?.toISOString() })
        ?.eq('id', requestId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status. Please try again.');
    }
  };

  const handleViewDetails = (requestId) => {
    navigate('/service-request-detail-updates', { state: { requestId } });
  };

  const getMetrics = () => {
    const today = new Date();
    today?.setHours(0, 0, 0, 0);

    const todayRequests = requests?.filter(req => {
      const reqDate = new Date(req?.created_at);
      reqDate?.setHours(0, 0, 0, 0);
      return reqDate?.getTime() === today?.getTime();
    });

    return {
      total: filteredRequests?.length,
      pending: filteredRequests?.filter(req => req?.status === 'pending')?.length,
      inProgress: filteredRequests?.filter(req => req?.status === 'in-progress')?.length,
      completed: todayRequests?.filter(req => req?.status === 'completed')?.length
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminHeader />
        <main className="pt-16">
          <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
            <div className="text-center">
              <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading requests...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader />
      <main className="pt-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <Icon name="Wrench" size={28} color="var(--color-primary)" strokeWidth={2} />
                Technician Portal
              </h1>
              <p className="text-sm text-muted-foreground mt-1">Manage assigned service requests and field operations</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={handlePullToRefresh}
              loading={refreshing}
            >
              Refresh
            </Button>
          </div>

          {/* Metrics Bar */}
          <MetricsBar metrics={getMetrics()} />

          {/* Filter Bar */}
          <FilterBar filters={filters} onFilterChange={setFilters} />

          {/* Request Queue */}
          <div className="mt-6">
            {filteredRequests?.length === 0 ? (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <Icon name="Inbox" size={48} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No Service Requests</h3>
                <p className="text-sm text-muted-foreground">No requests match your current filters.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredRequests?.map(request => (
                  <RequestQueueCard
                    key={request?.id}
                    request={request}
                    onStatusUpdate={handleStatusUpdate}
                    onViewDetails={handleViewDetails}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default TechnicianPortalDashboard;